import { useLogin } from 'hooks/useLogin';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Stack, styled } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { MobilePageContainer } from 'components/views/mobile/MobilePageContainer';
import { RoutePaths } from 'constants/RoutePaths';
import { useForm } from 'react-hook-form';

const StyledErrorDiv = styled('div')(({ theme }) => ({
  color: theme.palette.error.main,
}));

const StyledFormStack = styled(Stack)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(4),
}));

type LoginFormValues = {
  email: string;
  password: string;
};

export const LoginPageMobile = () => {
    const [showPassword, setShowPassword] = useState(false);
    const { login, loginPending } = useLogin();
    const { t } = useTranslation();

    const { register, handleSubmit, formState: { errors } } = useForm<LoginFormValues>({
      mode: 'onBlur',
    });

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event: React.MouseEvent) => event.preventDefault();

    const onSubmit = async (data: LoginFormValues) => {
      await login({ email: data.email, password: data.password });
    };

    return (
      <MobilePageContainer
        title={t('loginPage.logIn')}
        footerText={t('loginPage.loginProblem')}
        footerUrl={RoutePaths.FORGOT_PASSWORD}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <StyledFormStack>
            <FormControl variant="outlined">
              <InputLabel htmlFor="email">{t('loginPage.email')}</InputLabel>
              <OutlinedInput
                id="email"
                label={t('loginPage.email')}
                inputProps={{ "data-testid": "login-email-input" }}
                {...register('email', { required: t('loginPage.errors.loginRequired') })}
              />
              {errors.email && <StyledErrorDiv>{errors.email.message}</StyledErrorDiv>}
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel htmlFor="password">{t('loginPage.password')}</InputLabel>
              <OutlinedInput
                id="password"
                label={t('loginPage.password')}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{ "data-testid": "login-password-input" }}
                {...register('password', { required: t('loginPage.errors.passwordRequired') })}
              />
              {errors.password && <StyledErrorDiv>{errors.password.message}</StyledErrorDiv>}
            </FormControl>
            <LoadingButton
              data-testid="login-submit-button"
              loading={loginPending}
              variant='contained'
              color="primary"
              type="submit">
                {t('loginPage.loginButtonText')}
            </LoadingButton>
          </StyledFormStack>
        </form>
      </MobilePageContainer>
    );
  };
